import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MarketingNav } from '@/components/landing/MarketingNav';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { BookOpen, Plug, BarChart3, Settings, ArrowRight, Zap } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { SchemaMarkup, generateBreadcrumbSchema } from '@/components/SchemaMarkup';
import { Breadcrumbs } from '@/components/Breadcrumbs';

import { documentationCategories, getArticlesByCategory } from '@/data/documentationArticles';

const categoryIcons = [Zap, BarChart3, Plug, Settings];

export default function DocumentationPage() {
  const { i18n } = useTranslation();
  const isPt = i18n.language?.startsWith('pt');

  const categories = documentationCategories.map((cat, i) => ({
    ...cat,
    icon: categoryIcons[i] || BookOpen,
    articles: getArticlesByCategory(cat.key),
  }));

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={isPt ? "Documentação | RevOps AI — Central de Ajuda" : "Documentation | RevOps AI — Help Center"}
        description={isPt ? "Tudo que você precisa saber para aproveitar ao máximo o RevOps AI. Guias, tutoriais e referência técnica." : "Everything you need to know to get the most out of RevOps AI. Guides, tutorials and technical reference."}
        path="/documentation"
      />
      <SchemaMarkup schema={[generateBreadcrumbSchema([
        { name: 'Home', url: 'https://revops.digitais.ai/' },
        { name: isPt ? 'Documentação' : 'Documentation', url: 'https://revops.digitais.ai/documentation' },
      ])]} />
      <MarketingNav />
      <main>
        <section className="py-20 md:py-28 bg-[#F4F5FF]">
          <div className="container max-w-3xl mx-auto">
            <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: isPt ? 'Documentação' : 'Documentation' }]} />
            <div className="text-center">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-[#4F6CFF]/10 rounded-full text-sm font-medium text-[#4F6CFF]">
              <BookOpen className="h-4 w-4" />
              {isPt ? 'Documentação' : 'Documentation'}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A1F3C] mb-6">
              {isPt ? 'Central de Documentação' : 'Documentation Center'}
            </h1>
            <p className="text-lg text-[#1A1F3C]/60 mb-4">
              {isPt
                ? 'Tudo que você precisa saber para aproveitar ao máximo o RevOps AI.'
                : 'Everything you need to know to get the most out of RevOps AI.'}
            </p>
            <p className="text-sm text-[#1A1F3C]/40 max-w-2xl mx-auto">
              {isPt
                ? 'Guias passo a passo para criar sua conta, conectar seu CRM (HubSpot ou RD Station), configurar pipelines, mapear campos, definir metas e explorar dashboards de Revenue Intelligence, forecast e análise de funil.'
                : 'Step-by-step guides to create your account, connect your CRM (HubSpot or RD Station), configure pipelines, map fields, set targets and explore Revenue Intelligence dashboards, forecasting and funnel analysis.'}
            </p>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="container max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {categories.map((cat, i) => {
                const Icon = cat.icon;
                return (
                  <div key={i} className="rounded-xl border border-[#1A1F3C]/10 p-6 hover:border-[#4F6CFF]/30 transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-lg bg-[#4F6CFF]/10">
                        <Icon className="h-5 w-5 text-[#4F6CFF]" />
                      </div>
                      <h3 className="text-lg font-bold text-[#1A1F3C]">{isPt ? cat.title.pt : cat.title.en}</h3>
                    </div>
                    <ul className="space-y-2">
                      {cat.articles.map((article) => (
                        <li key={article.slug}>
                          <Link
                            to={`/documentation/${article.slug}`}
                            className="flex items-center gap-2 text-sm text-[#1A1F3C]/60 hover:text-[#4F6CFF] transition-colors"
                          >
                            <ArrowRight className="h-3 w-3 flex-shrink-0" />
                            {isPt ? article.title.pt : article.title.en}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 bg-[#F4F5FF]/50 text-center">
          <div className="container max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-[#1A1F3C] mb-4">
              {isPt ? 'Não encontrou o que procurava?' : "Didn't find what you were looking for?"}
            </h2>
            <p className="text-[#1A1F3C]/60 mb-6">
              {isPt ? 'Nossa equipe de suporte está pronta para ajudar.' : 'Our support team is ready to help.'}
            </p>
            <Link to="/help" className="inline-flex items-center gap-2 text-[#4F6CFF] font-medium hover:underline">
              {isPt ? 'Ir para Central de Ajuda' : 'Go to Help Center'}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>
      <LandingFooter />
    </div>
  );
}
